import React, { useState, useEffect, useCallback } from 'react';
import { ProductDetail, PhysicalData, TemperatureValue, TemperatureRange, ValueRange } from '../../../../types/ProductTypes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import { SidebarCard, SidebarTitle, SidebarContent, SidebarItem } from '../../Sidebar.styles';
import {
    CompactInput35,
    CompactInput55,
    CompactInput70,
    CompactRangeInput40,
    CompactRangeInput45,
    RangeInputWrapper,
    UnitLabel35,
    UnitLabel45,
    CompactSelect,
} from '../../EditableFields.styles';
import { normalizeNumericInput } from '../../../tools/conversionUtils';

// Yes/No/Unknown enum values (matching old implementation)
const YES_NO_UNKNOWN_OPTIONS = [
    { value: 0, label: 'Unknown' },
    { value: 1, label: 'Yes' },
    { value: 2, label: 'No' },
];

// Origin options (matching old implementation)
const ORIGIN_OPTIONS = [
    'Petroleum',
    'Bio-mass (vegetable)',
    'Bio-mass (animal)',
    'Coal',
    'Inorganic (from petroleum/LNG)',
    'Inorganic (phosphate)',
    'Inorganic (brine)',
    'Inorganic (potassium salt)',
    'Check',
];

// Helper to map Yes/No/Unknown enum to string
const yesNoUnknownToString = (value: number | null | undefined): string => {
    switch (value) {
        case 1:
            return 'Yes';
        case 2:
            return 'No';
        default:
            return 'Unknown';
    }
};

// Form state interface for local editing
interface PhysicalPropertiesFormState {
    litreWeight: string;
    litreWeightRefTemp: string;
    litreWeightCorrectionFactor: string;
    meltingPointMin: string;
    meltingPointMax: string;
    boilingPointMin: string;
    boilingPointMax: string;
    flashPointMin: string;
    flashPointMax: string;
    viscosityMin: string;
    viscosityMax: string;
    viscosityRefTemp: string;
    vapourPressureMin: string;
    vapourPressureMax: string;
    vapourPressureRefTemp: string;
    odorThreshold: string;
    phMin: string;
    phMax: string;
    origin: string;
    persistentFloater: number;
}

// Helper to parse numeric value from string, returns null for empty/invalid
const parseNumericValue = (value: string): number | null => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
};

// Helper to create TemperatureValue from number
const createTempValue = (value: number | null): TemperatureValue | null => {
    return value !== null ? { value } : null;
};

// Helper to create TemperatureRange from min/max strings
const createTempRange = (minStr: string, maxStr: string): TemperatureRange | null => {
    const minVal = parseNumericValue(minStr);
    const maxVal = parseNumericValue(maxStr);
    if (minVal === null && maxVal === null) return null;
    return {
        minValue: createTempValue(minVal),
        maxValue: createTempValue(maxVal),
    };
};

// Helper to create ValueRange from min/max strings
const createValueRange = (minStr: string, maxStr: string): ValueRange | null => {
    const minVal = parseNumericValue(minStr);
    const maxVal = parseNumericValue(maxStr);
    if (minVal === null && maxVal === null) return null;
    return {
        minValue: minVal,
        maxValue: maxVal,
    };
};

interface PhysicalPropertiesSectionProps {
    product: ProductDetail;
}

const PhysicalPropertiesSection: React.FC<PhysicalPropertiesSectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const showEditUI = canEdit && isInEditMode;

    // Initialize form state from product data
    const initializeFormState = useCallback((): PhysicalPropertiesFormState => {
        const pd = product.physicalData;
        return {
            litreWeight: pd?.litreWeight?.value?.toString() ?? '',
            litreWeightRefTemp: pd?.litreWeightReferenceTemperature?.value?.toString() ?? '',
            litreWeightCorrectionFactor: pd?.litreWeightCorrectionFactor?.toString() ?? '',
            meltingPointMin: pd?.meltingPointRange?.minValue?.value?.toString() ?? '',
            meltingPointMax: pd?.meltingPointRange?.maxValue?.value?.toString() ?? '',
            boilingPointMin: pd?.boilingPointRange?.minValue?.value?.toString() ?? '',
            boilingPointMax: pd?.boilingPointRange?.maxValue?.value?.toString() ?? '',
            flashPointMin: pd?.flashPointRange?.minValue?.value?.toString() ?? '',
            flashPointMax: pd?.flashPointRange?.maxValue?.value?.toString() ?? '',
            viscosityMin: pd?.viscosityRange?.minValue?.toString() ?? '',
            viscosityMax: pd?.viscosityRange?.maxValue?.toString() ?? '',
            viscosityRefTemp: pd?.viscosityReferenceTemperature?.value?.toString() ?? '',
            vapourPressureMin: pd?.vapourPressureRange?.minValue?.toString() ?? '',
            vapourPressureMax: pd?.vapourPressureRange?.maxValue?.toString() ?? '',
            vapourPressureRefTemp: pd?.vapourPressureReferenceTemperature?.value?.toString() ?? '',
            odorThreshold: pd?.odorThreshold?.toString() ?? '',
            phMin: pd?.phRange?.minValue?.toString() ?? '',
            phMax: pd?.phRange?.maxValue?.toString() ?? '',
            origin: pd?.origin ?? '',
            persistentFloater: product.regulations?.persistentFloater ?? 0,
        };
    }, [product]);

    const [formState, setFormState] = useState<PhysicalPropertiesFormState>(initializeFormState);

    // Reset form state when product changes or edit mode is exited
    useEffect(() => {
        setFormState(initializeFormState());
    }, [product, isInEditMode, initializeFormState]);

    // Build PhysicalData object from form state
    const buildPhysicalData = useCallback(
        (currentFormState: PhysicalPropertiesFormState): PhysicalData => {
            const existingPd = product.physicalData;
            return {
                litreWeight: createTempValue(parseNumericValue(currentFormState.litreWeight)),
                litreWeightReferenceTemperature: createTempValue(parseNumericValue(currentFormState.litreWeightRefTemp)),
                litreWeightCorrectionFactor: parseNumericValue(currentFormState.litreWeightCorrectionFactor),
                meltingPointRange: createTempRange(currentFormState.meltingPointMin, currentFormState.meltingPointMax),
                boilingPointRange: createTempRange(currentFormState.boilingPointMin, currentFormState.boilingPointMax),
                flashPointRange: createTempRange(currentFormState.flashPointMin, currentFormState.flashPointMax),
                viscosityRange: createValueRange(currentFormState.viscosityMin, currentFormState.viscosityMax),
                viscosityReferenceTemperature: createTempValue(parseNumericValue(currentFormState.viscosityRefTemp)),
                temperatureToAchieve50mPas: existingPd?.temperatureToAchieve50mPas ?? null,
                vapourPressureRange: createValueRange(currentFormState.vapourPressureMin, currentFormState.vapourPressureMax),
                vapourPressureReferenceTemperature: createTempValue(parseNumericValue(currentFormState.vapourPressureRefTemp)),
                solubleInWater: existingPd?.solubleInWater ?? null,
                isStaticAccumulator: existingPd?.isStaticAccumulator ?? null,
                phRange: createValueRange(currentFormState.phMin, currentFormState.phMax),
                thresholdLimitValueTimeWeightedAverage: existingPd?.thresholdLimitValueTimeWeightedAverage ?? null,
                odorThreshold: parseNumericValue(currentFormState.odorThreshold),
                vapourDensity: existingPd?.vapourDensity ?? null,
                origin: currentFormState.origin || null,
            };
        },
        [product.physicalData]
    );

    // Handle field changes and merge to pending changes
    const handleFieldChange = useCallback(
        (field: keyof PhysicalPropertiesFormState, value: string | number) => {
            // Normalize comma to dot for numeric string inputs (European locale support)
            if (typeof value === 'string' && field !== 'origin') {
                value = normalizeNumericInput(value);
            }
            setFormState((prev) => {
                const newState = { ...prev, [field]: value };

                // Build and merge pending changes
                if (mergePendingChanges) {
                    const physicalData = buildPhysicalData(newState);

                    // Handle persistentFloater separately (it's in regulations, not physicalData)
                    if (field === 'persistentFloater') {
                        mergePendingChanges({
                            physicalData,
                            regulations: {
                                ...product.regulations,
                                persistentFloater: value as number,
                            },
                        });
                    } else {
                        mergePendingChanges({ physicalData });
                    }
                }

                return newState;
            });
        },
        [mergePendingChanges, buildPhysicalData, product.regulations]
    );

    if (!product.physicalData) {
        return null;
    }

    const pd = product.physicalData;

    // Helper function to render range presentation (matching old implementation)
    const renderRange = (min: number | null | undefined, max: number | null | undefined, unit: string): string => {
        const hasMin = min !== null && min !== undefined;
        const hasMax = max !== null && max !== undefined;

        if (!hasMin && !hasMax) return ' ';
        if (!hasMin) return `< ${max} ${unit}`;
        if (!hasMax) return `> ${min} ${unit}`;
        if (min === max) return `${min} ${unit}`;
        return `${min} ${unit} / ${max} ${unit}`;
    };

    // Helper for temperature ranges (extracts value from object)
    const renderTempRange = (min: any, max: any): string => {
        const minVal = min?.value;
        const maxVal = max?.value;
        const hasMin = minVal !== null && minVal !== undefined;
        const hasMax = maxVal !== null && maxVal !== undefined;

        if (!hasMin && !hasMax) return ' ';
        if (!hasMin) return `< ${maxVal}°C`;
        if (!hasMax) return `> ${minVal}°C`;
        if (minVal === maxVal) return `${minVal}°C`;
        return `${minVal}°C / ${maxVal}°C`;
    };

    // Calculate Persistent Floater based on USCG group and boiling point
    // (matching old implementation: getCalculatedValueForPersistentFloater)
    const getCalculatedPersistentFloater = (): string => {
        const uscgGroup = product.regulations?.uscgGroup;
        const boilingPointMin = pd.boilingPointRange?.minValue?.value;
        const boilingPointMax = pd.boilingPointRange?.maxValue?.value;

        // Check if USCG group is defined
        if (!uscgGroup || uscgGroup.number === undefined || uscgGroup.number === null) {
            return 'No';
        }

        // Check if it's a possible floater group (30, 31, 32, 33)
        const floaterGroups = [30, 31, 32, 33];
        if (!floaterGroups.includes(uscgGroup.number)) {
            return 'No';
        }

        // Check if boiling point is over 340
        if (
            (boilingPointMin !== null && boilingPointMin !== undefined && boilingPointMin >= 340) ||
            (boilingPointMax !== null && boilingPointMax !== undefined && boilingPointMax >= 340)
        ) {
            return 'Yes';
        }

        return 'No';
    };

    return (
        <SidebarCard>
            <SidebarTitle>Physical Properties</SidebarTitle>
            <SidebarContent>
                {/* Litre Weight */}
                {(pd.litreWeight || showEditUI) && (
                    <SidebarItem>
                        <label title="The weight of one litre of the product at the stated reference temperature">Litre Weight ({formState.litreWeightRefTemp || pd.litreWeightReferenceTemperature?.value || 20}°C):</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactInput55
                                    type="text"
                                    value={formState.litreWeight}
                                    onChange={(e) => handleFieldChange('litreWeight', e.target.value)}
                                />
                                <UnitLabel35>kg/L</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{pd.litreWeight?.value} kg/L</span>
                        )}
                    </SidebarItem>
                )}

                {/* Litre Weight Reference Temperature - Edit Mode Only */}
                {showEditUI && (pd.litreWeight || formState.litreWeight) && (
                    <SidebarItem>
                        <label title="The reference temperature for which the weight of one litre of the product is stated">Ref. Temp.:</label>
                        <RangeInputWrapper>
                            <CompactInput35
                                type="text"
                                value={formState.litreWeightRefTemp}
                                onChange={(e) => handleFieldChange('litreWeightRefTemp', e.target.value)}
                            />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    </SidebarItem>
                )}

                {(pd.litreWeightCorrectionFactor !== null || showEditUI) && (
                    <SidebarItem>
                        <label title="Volume correction factor per 1°C change in temperature">Correction Factor:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactInput70
                                    type="text"
                                    value={formState.litreWeightCorrectionFactor}
                                    onChange={(e) => handleFieldChange('litreWeightCorrectionFactor', e.target.value)}
                                />
                                <UnitLabel35></UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{pd.litreWeightCorrectionFactor || ' '}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Melting Point */}
                {(pd.meltingPointRange || showEditUI) && (
                    <SidebarItem>
                        <label title="The temperature at which the product changes from solid to liquid">Melting Point:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.meltingPointMin}
                                    onChange={(e) => handleFieldChange('meltingPointMin', e.target.value)}
                                />
                                <span>-</span>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.meltingPointMax}
                                    onChange={(e) => handleFieldChange('meltingPointMax', e.target.value)}
                                />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderTempRange(pd.meltingPointRange?.minValue, pd.meltingPointRange?.maxValue)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Boiling Point */}
                {(pd.boilingPointRange || showEditUI) && (
                    <SidebarItem>
                        <label title="The temperature at which the product changes from liquid to gas">Boiling Point:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.boilingPointMin}
                                    onChange={(e) => handleFieldChange('boilingPointMin', e.target.value)}
                                />
                                <span>-</span>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.boilingPointMax}
                                    onChange={(e) => handleFieldChange('boilingPointMax', e.target.value)}
                                />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderTempRange(pd.boilingPointRange?.minValue, pd.boilingPointRange?.maxValue)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Flash Point */}
                {(pd.flashPointRange || showEditUI) && (
                    <SidebarItem>
                        <label title="The lowest temperature at which the product's vapour can form an ignitable mixture">Flash Point:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.flashPointMin}
                                    onChange={(e) => handleFieldChange('flashPointMin', e.target.value)}
                                />
                                <span>-</span>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.flashPointMax}
                                    onChange={(e) => handleFieldChange('flashPointMax', e.target.value)}
                                />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderTempRange(pd.flashPointRange?.minValue, pd.flashPointRange?.maxValue)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Viscosity */}
                {(pd.viscosityRange || showEditUI) && (
                    <SidebarItem>
                        <label title="The measure of a liquid's resistance to flow (water is 1 mPa.s)">Viscosity <i>{formState.viscosityRefTemp || pd.viscosityReferenceTemperature?.value || ''} °C</i>:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.viscosityMin}
                                    onChange={(e) => handleFieldChange('viscosityMin', e.target.value)}
                                />
                                <span>-</span>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.viscosityMax}
                                    onChange={(e) => handleFieldChange('viscosityMax', e.target.value)}
                                />
                                <UnitLabel45>mPa.s</UnitLabel45>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.viscosityRange?.minValue, pd.viscosityRange?.maxValue, 'mPa.s')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Viscosity Reference Temperature - Edit Mode Only */}
                {showEditUI && (pd.viscosityRange || formState.viscosityMin || formState.viscosityMax) && (
                    <SidebarItem>
                        <label title="The reference temperature for which the stated viscosity is a valid measure">Ref. Temp.:</label>
                        <RangeInputWrapper>
                            <CompactInput35
                                type="text"
                                value={formState.viscosityRefTemp}
                                onChange={(e) => handleFieldChange('viscosityRefTemp', e.target.value)}
                            />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    </SidebarItem>
                )}

                {/* Vapour Pressure */}
                {(pd.vapourPressureRange || showEditUI) && (
                    <SidebarItem>
                        <label title="The pressure above the liquid">Vapour Pressure:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.vapourPressureMin}
                                    onChange={(e) => handleFieldChange('vapourPressureMin', e.target.value)}
                                />
                                <span>-</span>
                                <CompactRangeInput45
                                    type="text"
                                    value={formState.vapourPressureMax}
                                    onChange={(e) => handleFieldChange('vapourPressureMax', e.target.value)}
                                />
                                <UnitLabel35>kPa</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.vapourPressureRange?.minValue, pd.vapourPressureRange?.maxValue, 'kPa')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Vapour Pressure Reference Temperature - Edit Mode Only */}
                {showEditUI && (pd.vapourPressureRange || formState.vapourPressureMin || formState.vapourPressureMax) && (
                    <SidebarItem>
                        <label title="The reference temperature for which the stated vapour pressure is a valid measure">Ref. Temp.:</label>
                        <RangeInputWrapper>
                            <CompactInput35
                                type="text"
                                value={formState.vapourPressureRefTemp}
                                onChange={(e) => handleFieldChange('vapourPressureRefTemp', e.target.value)}
                            />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    </SidebarItem>
                )}

                {/* Odor Threshold */}
                {(pd.odorThreshold !== null || showEditUI) && (
                    <SidebarItem>
                        <label title="The smallest concentration of gas you can detect by smell (nose)">Odor Threshold:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactInput55
                                    type="text"
                                    value={formState.odorThreshold}
                                    onChange={(e) => handleFieldChange('odorThreshold', e.target.value)}
                                />
                                <UnitLabel35>ppm</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.odorThreshold, pd.odorThreshold, 'ppm')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* PH */}
                {(pd.phRange || showEditUI) && (
                    <SidebarItem>
                        <label title="Potenz Hydrogen is a unit for measuring acidity or alkalinity of a solution">PH:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput40
                                    type="text"
                                    value={formState.phMin}
                                    onChange={(e) => handleFieldChange('phMin', e.target.value)}
                                />
                                <span>-</span>
                                <CompactRangeInput40
                                    type="text"
                                    value={formState.phMax}
                                    onChange={(e) => handleFieldChange('phMax', e.target.value)}
                                />
                                <UnitLabel35></UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.phRange?.minValue, pd.phRange?.maxValue, '')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Inhibited - read-only, comes from category */}
                <SidebarItem>
                    <label title="Indicates if an inhibitor should be added in accordance with IBC 15.13">Inhibited:</label>
                    <span>{product.category?.isInhibited ? 'Yes' : 'No'}</span>
                </SidebarItem>

                {/* Insurance Persistent Floater - dropdown in edit mode */}
                <SidebarItem>
                    <label title="Indicates if the product is a persistent floater">Insurance Persistent Floater:</label>
                    {showEditUI ? (
                        <CompactSelect
                            title="Insurance Persistent Floater"
                            value={formState.persistentFloater}
                            onChange={(e) => handleFieldChange('persistentFloater', parseInt(e.target.value, 10))}
                        >
                            {YES_NO_UNKNOWN_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{yesNoUnknownToString(product.regulations?.persistentFloater)}</span>
                    )}
                </SidebarItem>

                {/* Calculated Persistent Floater - only shown in edit mode */}
                {showEditUI && (
                    <SidebarItem>
                        <label title="Indicates if the product could be a persistent floater based on its USCG Group and its Boiling Point">Calc. Persistent Floater:</label>
                        <span>{getCalculatedPersistentFloater()}</span>
                    </SidebarItem>
                )}

                {/* Origin - dropdown in edit mode */}
                {(pd.origin || showEditUI) && (
                    <SidebarItem>
                        <label title="Origin of the product">Origin:</label>
                        {showEditUI ? (
                            <CompactSelect
                                title="Origin"
                                value={formState.origin}
                                onChange={(e) => handleFieldChange('origin', e.target.value)}
                            >
                                <option value="">-- Select --</option>
                                {ORIGIN_OPTIONS.map((origin) => (
                                    <option key={origin} value={origin}>
                                        {origin}
                                    </option>
                                ))}
                            </CompactSelect>
                        ) : (
                            <span>{pd.origin}</span>
                        )}
                    </SidebarItem>
                )}
            </SidebarContent>
        </SidebarCard>
    );
};

export default PhysicalPropertiesSection;
